import { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  ButtonBase,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { psiTokens } from '../theme/psiTheme';
import { usePageMeta } from '../hooks/usePageMeta';

// Rendered by DynamicRecordPage once it has resolved a URL slug to a class
// record (see src/data/classes and src/pages/DynamicRecordPage.jsx).
export function ClassRecordContent({ record }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  usePageMeta(`${record.id} · ${record.programName}`, record.intro);

  const filteredParticipants = useMemo(() => {
    const q = filter.trim().toUpperCase();
    if (!q) return record.participants;
    return record.participants.filter((p) => p.id.toUpperCase().includes(q));
  }, [filter, record.participants]);

  return (
    <Box sx={{ backgroundColor: psiTokens.background.primary }}>
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        {/* Page header */}
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: psiTokens.primary.light,
            mb: 1.5,
          }}
        >
          Class Record
        </Typography>

        <Typography variant="h2" sx={{ color: psiTokens.text.primary, fontWeight: 700, mb: 1.5 }}>
          {record.id} · <Box component="span" sx={{ color: psiTokens.primary.dark }}>{record.programName}</Box>
        </Typography>

        <Typography sx={{ fontSize: '14px', color: psiTokens.text.tertiary, fontWeight: 400, mb: 2 }}>
          {record.sessionsCount} sessions &nbsp;·&nbsp; {record.dateRange} &nbsp;·&nbsp; {record.totalHours} &nbsp;·&nbsp; Instructed
          by <Box component="span" sx={{ fontWeight: 600, color: psiTokens.text.secondary }}>{record.instructor}</Box>
        </Typography>

        <Box
          sx={{
            width: '48px',
            height: '3px',
            background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
            borderRadius: '2px',
            my: 2.5,
          }}
        />

        <Typography sx={{ fontSize: '14px', color: psiTokens.text.tertiary, lineHeight: 1.8, maxWidth: '720px', mb: 6 }}>
          {record.intro}
        </Typography>

        {/* Topics */}
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: psiTokens.text.tertiary,
            mb: 2.5,
          }}
        >
          Topics Covered
        </Typography>

        <Grid container spacing={2} sx={{ mb: 7 }}>
          {record.topics.map((topic) => (
            <Grid item xs={12} sm={6} md={3} key={topic.session}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  backgroundColor: psiTokens.background.tertiary,
                  border: `1px solid ${psiTokens.border}`,
                  borderLeft: `3px solid ${psiTokens.primary.dark}`,
                  borderRadius: '10px',
                }}
              >
                <CardContent sx={{ p: '18px 20px' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: psiTokens.primary.light, mb: 0.5 }}>
                    {topic.session}
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: psiTokens.text.tertiary, mb: 1 }}>
                    {topic.date}
                  </Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: psiTokens.text.primary, lineHeight: 1.3, mb: 1 }}>
                    {topic.name}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 400, color: psiTokens.text.secondary, lineHeight: 1.6 }}>
                    {topic.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Participants */}
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: psiTokens.text.tertiary,
            mb: 2.5,
          }}
        >
          Participant Attendance
        </Typography>

        <ButtonBase
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            backgroundColor: psiTokens.background.tertiary,
            border: `1px solid ${psiTokens.border}`,
            borderRadius: '10px',
            padding: '18px 22px',
            textAlign: 'left',
            '&:hover': { backgroundColor: psiTokens.background.secondary },
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: psiTokens.text.primary }}>
              Certificate Verification & Attendance Log
            </Typography>
            <Typography sx={{ fontSize: '12px', color: psiTokens.text.tertiary, fontWeight: 400 }}>
              {record.participants.length} participants &nbsp;·&nbsp; Record IDs only &nbsp;·&nbsp; Click to {open ? 'collapse' : 'expand'}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: psiTokens.primary.dark,
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {open ? <CloseIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
          </Box>
        </ButtonBase>

        {open && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: psiTokens.text.tertiary }}>
                Filter by ID
              </Typography>
              <TextField
                size="small"
                placeholder="e.g. SN1025"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ width: 200, '& .MuiOutlinedInput-root': { backgroundColor: psiTokens.background.primary } }}
              />
              <Typography sx={{ fontSize: '12px', color: psiTokens.text.tertiary }}>
                Showing {filteredParticipants.length} of {record.participants.length}
              </Typography>
            </Box>

            <Box sx={{ overflowX: 'auto', border: `1px solid ${psiTokens.border}`, borderRadius: '10px' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <Box component="thead">
                  <Box component="tr" sx={{ backgroundColor: psiTokens.background.tertiary }}>
                    <HeaderCell align="left">Record ID</HeaderCell>
                    <HeaderCell>Training Hrs</HeaderCell>
                    <HeaderCell>Participation</HeaderCell>
                    {record.topics.map((t) => (
                      <HeaderCell key={t.session}>{t.session}</HeaderCell>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {filteredParticipants.map((p) => (
                    <Box
                      component="tr"
                      key={p.id}
                      sx={{
                        borderBottom: `1px solid ${psiTokens.border}`,
                        '&:hover': { backgroundColor: psiTokens.background.tertiary },
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <Box component="td" sx={{ p: '10px 14px', fontWeight: 700, color: psiTokens.text.primary, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {p.id}
                      </Box>
                      <Box component="td" sx={{ p: '10px 14px', textAlign: 'center', color: psiTokens.text.secondary }}>
                        {p.hrs}
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          p: '10px 14px',
                          textAlign: 'center',
                          fontWeight: 600,
                          color: parseInt(p.pct) >= 60 ? psiTokens.success : psiTokens.error,
                        }}
                      >
                        {p.pct}
                      </Box>
                      {p.s.map((attended, idx) => (
                        <Box
                          component="td"
                          key={idx}
                          sx={{
                            p: '10px 14px',
                            textAlign: 'center',
                            color: attended === 2 ? psiTokens.primary.dark : psiTokens.borderLight,
                            fontWeight: attended === 2 ? 600 : 400,
                          }}
                          title={attended === 2 ? 'Attended (2 hrs)' : 'Absent'}
                        >
                          {attended === 2 ? '●' : '–'}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export function HeaderCell({ children, align = 'center' }) {
  return (
    <Box
      component="th"
      sx={{
        p: '10px 14px',
        textAlign: align,
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: psiTokens.text.tertiary,
        borderBottom: `1px solid ${psiTokens.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
}
